<template>
  <div
    class="ach-badge"
    :class="[`level-${level}`, { 'is-locked': locked, 'is-legend': isLegend && !locked }]"
    :style="rootStyle"
  >
    <svg class="ach-badge-svg" viewBox="0 0 100 100" aria-hidden="true">
      <defs>
        <!-- 只有极轻微的上浅下深（约 4%），避免出现「金属渐变」的反光感 -->
        <linearGradient :id="gradId" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" :stop-color="palette.from" />
          <stop offset="100%" :stop-color="palette.to" />
        </linearGradient>
        <filter :id="shadowId" x="-35%" y="-35%" width="170%" height="180%">
          <feGaussianBlur stdDeviation="3.2" />
        </filter>
      </defs>

      <!-- 贴纸落地投影（下移 + 高斯模糊，做出「贴上去」的厚度感） -->
      <path
        class="badge-shadow"
        :d="BODY_PATH"
        :filter="`url(#${shadowId})`"
        transform="translate(0 3.2)"
      />

      <!-- 外六边形：暖棕描边 + 奶油底 = 「棕线 + 奶油环」的贴纸轮廓 -->
      <path
        class="badge-body"
        :d="BODY_PATH"
        :fill="ringFill"
        :stroke="outlineColor"
        stroke-width="2.6"
        stroke-linejoin="round"
      />
      <!-- 内六边形：等级纯色 -->
      <path class="badge-inner" :d="INNER_PATH" :fill="`url(#${gradId})`" />

      <!-- 传奇徽章的点缀光点 -->
      <template v-if="isLegend && !locked">
        <circle class="badge-spark" cx="26" cy="30" r="2.6" />
        <circle class="badge-spark" cx="76" cy="34" r="1.9" />
        <circle class="badge-spark" cx="70" cy="72" r="2.1" />
        <circle class="badge-spark" cx="31" cy="68" r="1.6" />
      </template>
    </svg>

    <!-- 图标（叠在内六边形上，等比方框内居中，不会超出徽章）。
         图标数据来自 Font Awesome 图标库，统一由 FaIcon 渲染，禁止手写 <svg> 画图标。 -->
    <FaIcon
      class="ach-badge-icon"
      :icon="resolvedIcon"
      :size="iconSize"
      :style="iconStyle"
    />
  </div>
</template>

<script>
import { resolveAchievementIcon } from './achievementIcons.js'
import FaIcon from '@/components/common/FaIcon.vue'

/**
 * 徽章 SVG 形状（viewBox 0 0 100 100）。
 *
 * 六边形（尖顶）+ 圆角：每个顶点沿两条边各回退一段后用二次贝塞尔连过顶点，
 * 比 `stroke-linejoin:round` 的圆角更大、更「贴纸」。
 * BODY_PATH 为外轮廓（R=46），INNER_PATH 为内轮廓（R=35.5），两者之间就是奶油色描边环。
 */
const BODY_PATH = 'M41.34 9L18.82 22Q10.16 27 10.16 37L10.16 63Q10.16 73 18.82 78L41.34 91Q50 96 58.66 91L81.18 78Q89.84 73 89.84 63L89.84 37Q89.84 27 81.18 22L58.66 9Q50 4 41.34 9Z'
const INNER_PATH = 'M43.07 18.5L26.18 28.25Q19.26 32.25 19.26 40.25L19.26 59.75Q19.26 67.75 26.18 71.75L43.07 81.5Q50 85.5 56.93 81.5L73.82 71.75Q80.74 67.75 80.74 59.75L80.74 40.25Q80.74 32.25 73.82 28.25L56.93 18.5Q50 14.5 43.07 18.5Z'

/**
 * 各等级配色。
 *
 * 卡通贴纸风 = 纯色为主：`from` / `to` 只差约 4%，看上去基本是平涂，
 * 不会出现金属渐变那种「上亮下暗」的反光。
 */
const LEVEL_PALETTES = {
  1: { from: '#edb075', to: '#e0a05a' },
  2: { from: '#c0cbd8', to: '#a9b7c6' },
  3: { from: '#f9dc6e', to: '#f2cb4e' },
  4: { from: '#bda6e8', to: '#ab90dd' },
  5: { from: '#f79cba', to: '#ef82a8' }
}

/** 未解锁配色：中性灰。 */
const LOCKED_PALETTE = { from: '#dbe0dc', to: '#ccd4ce' }

/** 贴纸风描边色（外轮廓棕线 + 图标勾边）。 */
const OUTLINE = '#9a6b48'
/** 奶油色描边环。 */
const RING = '#fff6e6'

let uidSeed = 0

export default {
  name: 'AchievementBadge',
  components: { FaIcon },
  props: {
    /** 等级码 1~5。 */
    level: {
      type: Number,
      default: 1
    },
    /** 图标键（后端下发）。 */
    icon: {
      type: String,
      default: 'medal'
    },
    /** 是否未解锁。 */
    locked: {
      type: Boolean,
      default: false
    },
    /** 徽章边长（px）。 */
    size: {
      type: [Number, String],
      default: 72
    }
  },
  data() {
    const seed = ++uidSeed
    return {
      BODY_PATH,
      INNER_PATH,
      gradId: `ach-grad-${seed}`,
      shadowId: `ach-shadow-${seed}`
    }
  },
  computed: {
    levelNum() {
      const n = Number(this.level)
      return Number.isFinite(n) && n >= 1 && n <= 5 ? n : 1
    },
    isLegend() {
      return this.levelNum === 5
    },
    palette() {
      return this.locked ? LOCKED_PALETTE : (LEVEL_PALETTES[this.levelNum] || LEVEL_PALETTES[1])
    },
    /** 奶油色描边环（未解锁时用更冷的灰白，避免灰度后发黄）。 */
    ringFill() {
      return this.locked ? '#f3f4f2' : RING
    },
    outlineColor() {
      return this.locked ? '#a79c90' : OUTLINE
    },
    sizePx() {
      const n = Number(this.size)
      return Number.isFinite(n) && n > 0 ? n : 72
    },
    rootStyle() {
      return {
        width: `${this.sizePx}px`,
        height: `${this.sizePx}px`
      }
    },
    /**
     * 图标方框：边长取徽章的 38%，配合 FaIcon 自带的原始 viewBox + 默认 `xMidYMid meet`，
     * 任何长宽比的图标都会被完整装进这个方框，永远不会超出徽章。
     */
    iconStyle() {
      const s = this.sizePx * 0.38
      return {
        width: `${s.toFixed(2)}px`,
        height: `${s.toFixed(2)}px`,
        color: this.locked ? '#eceae3' : '#fffaf0'
      }
    },
    /** 图标边长（px），交给 FaIcon 的 size */
    iconSize() {
      return this.sizePx * 0.38
    },
    /** 图标定义（Font Awesome，来自 achievementIcons 的键值映射） */
    resolvedIcon() {
      return resolveAchievementIcon(this.icon)
    }
  }
}
</script>

<style scoped>
.ach-badge {
  position: relative;
  display: inline-block;
  flex: none;
  line-height: 0;
  transition: transform 0.25s ease, filter 0.25s ease;
}

.ach-badge-svg {
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.ach-badge-icon {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  fill: currentColor;
  pointer-events: none;
}

/* 图标勾边：贴纸风的暖棕描边（paint-order 让描边压在填充下，只露出外半圈）。
   ⚠️ path 在 FaIcon 子组件内部，scoped 样式必须用 `>>>` 才能穿透。 */
.ach-badge-icon >>> path {
  stroke: #8a5b3d;
  stroke-width: 26;
  stroke-linejoin: round;
  paint-order: stroke;
}

/* 未解锁：整体灰度 + 压暗 */
.ach-badge.is-locked {
  filter: grayscale(1);
  opacity: 0.55;
}

.ach-badge.is-locked .ach-badge-icon >>> path {
  stroke: #a79c90;
}

/* 落地投影 */
.badge-shadow {
  fill: rgba(139, 92, 58, 0.2);
}

.ach-badge.is-locked .badge-shadow {
  fill: rgba(90, 90, 90, 0.3);
}

.badge-spark {
  fill: rgba(255, 255, 255, 0.9);
  animation: ach-twinkle 2.2s ease-in-out infinite;
}

.badge-spark:nth-of-type(2) {
  animation-delay: 0.5s;
}

.badge-spark:nth-of-type(3) {
  animation-delay: 1s;
}

.badge-spark:nth-of-type(4) {
  animation-delay: 1.5s;
}

@keyframes ach-twinkle {
  0%, 100% { opacity: 0.25; }
  50% { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .badge-spark {
    animation: none;
  }
}
</style>
